import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Users, Search, MapPin, Phone, IndianRupee, ShoppingBag, Plus, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { staffSelfAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ListPageHeader from '../../components/shared/ListPageHeader';

const MyRetailersPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [retailers, setRetailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    activeToday: 0,
    pendingVisits: 0,
    totalOutstanding: 0,
  });

  useEffect(() => {
    fetchRetailers();
  }, []);

  const fetchRetailers = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await staffSelfAPI.getMyRetailers();
      setRetailers(data.retailers || []);
      setStats(data.stats || { total: 0, activeToday: 0, pendingVisits: 0, totalOutstanding: 0 });
    } catch (error) {
      console.error('Error fetching retailers:', error);
      const msg = getApiErrorMessage(error, 'Failed to load retailers. Please try again.');
      setError(msg);
      toast.error(msg);
      setRetailers([]);
      setStats({ total: 0, activeToday: 0, pendingVisits: 0, totalOutstanding: 0 });
    } finally {
      setLoading(false);
    }
  };

  const formatCompactCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount.toFixed(0)}`;
  };

  const filteredRetailers = retailers.filter(retailer => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      retailer.business_name?.toLowerCase().includes(search) ||
      retailer.contact_name?.toLowerCase().includes(search) ||
      retailer.mobile?.includes(search) ||
      retailer.area?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="h-full flex flex-col" data-testid="my-retailers-page">
      <ListPageHeader
        title="My Retailers"
        subtitle="Manage your assigned retailers and customers"
        stats={[
          { icon: Users, label: 'Total', value: stats.total || retailers.length, color: 'slate' },
          { icon: ShoppingBag, label: 'Active Today', value: stats.activeToday || stats.active_today || 0, color: 'emerald' },
          { icon: MapPin, label: 'Pending Visits', value: stats.pendingVisits || stats.pending_visits || 0, color: 'amber' },
          { icon: IndianRupee, label: 'Outstanding', value: formatCompactCurrency(stats.totalOutstanding || stats.total_outstanding), color: 'rose' },
        ]}
        ctaLabel="Add Retailer"
        ctaIcon={Plus}
        onCtaClick={() => navigate(`/business/${businessId}/customers/new`)}
      />

      {/* Controls Bar */}
      <div className="flex-shrink-0 px-2 pt-2">
        <div className="bg-white rounded-md border border-slate-200/60 shadow-sm p-3">
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name, phone or area..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full max-w-md pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table Area */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-[13px] text-slate-500 font-normal">Loading retailers...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center mb-4">
                <AlertCircle className="w-7 h-7 text-red-400" />
              </div>
              <p className="text-[14px] font-medium text-slate-700">Failed to load retailers</p>
              <p className="text-[13px] text-slate-400 mt-1">{error}</p>
              <button
                onClick={fetchRetailers}
                className="mt-4 px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700"
              >
                Retry
              </button>
            </div>
          ) : filteredRetailers.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <Users className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">
                {searchQuery ? 'No retailers found' : 'No retailers assigned yet'}
              </p>
              <p className="text-[13px] text-slate-400 mt-1">
                {searchQuery ? 'Try adjusting your search' : 'Contact your manager to get retailers assigned'}
              </p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto">
                <table className="tl-table table-fixed w-full">
                  <colgroup>
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '18%' }} />
                  </colgroup>
                  <thead className="sticky top-0 z-10">
                    <tr>
                      <th className="tl-th tl-th-teal">Retailer</th>
                      <th className="tl-th tl-th-teal">Contact</th>
                      <th className="tl-th tl-th-teal">Area</th>
                      <th className="tl-th tl-th-teal tl-th-right">Outstanding</th>
                      <th className="tl-th tl-th-teal">Last Visit</th>
                      <th className="tl-th tl-th-teal text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="animate-stagger">
                    {filteredRetailers.map((retailer, index) => (
                      <tr
                        key={retailer.id}
                        className={`tl-tr hover:bg-teal-50/40 animate-tableRowFadeIn cursor-pointer ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                        style={{ animationDelay: `${Math.min(index * 30, 300)}ms` }}
                        onClick={() => navigate(`/business/${businessId}/customers/${retailer.id}`)}
                      >
                        <td className="tl-td">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-[12px] font-medium text-teal-700">
                                {retailer.business_name?.charAt(0)?.toUpperCase() || '?'}
                              </span>
                            </div>
                            <div>
                              <p className="tl-cell-primary truncate">{retailer.business_name}</p>
                              <p className="text-[11px] text-slate-500">{retailer.contact_name || '---'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="tl-td">
                          <div className="flex items-center gap-1.5 text-[12px] text-slate-600">
                            <Phone className="w-3.5 h-3.5 text-slate-400" />
                            {retailer.mobile || '---'}
                          </div>
                        </td>
                        <td className="tl-td tl-cell-regular">
                          {retailer.area || '---'}
                        </td>
                        <td className="tl-td tl-td-right">
                          <span className={`tl-cell-amount ${(retailer.outstanding || 0) > 0 ? 'text-rose-600' : 'tl-cell-amount-positive'}`}>
                            ₹{(retailer.outstanding || 0).toLocaleString()}
                          </span>
                        </td>
                        <td className="tl-td tl-cell-regular">
                          {retailer.last_visit
                            ? new Date(retailer.last_visit).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
                            : '---'}
                        </td>
                        <td className="tl-td text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/business/${businessId}/sales/invoices/new?customer=${retailer.id}`);
                              }}
                              className="px-2.5 py-1.5 bg-white border border-slate-200 text-slate-700 rounded-md hover:bg-slate-50 transition-colors text-[11px] font-medium"
                            >
                              Invoice
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/business/${businessId}/payments-received/new?customer=${retailer.id}`);
                              }}
                              className="px-2.5 py-1.5 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors text-[11px] font-medium"
                            >
                              Collect
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                <p>
                  Showing <span className="font-medium text-slate-700">{filteredRetailers.length}</span> of <span className="font-medium text-slate-700">{retailers.length}</span> retailers
                </p>
                <p>
                  Outstanding: <span className="font-medium text-rose-600">{formatCompactCurrency(stats.totalOutstanding || stats.total_outstanding)}</span>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyRetailersPage;
